import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import Placeholder from './Placeholder.jsx';
import { ASK_EMAIL, seoRecsMode } from '../modules/index.js';
import SectionHeading from '../components/HowItWorksPanel.jsx';

// Session 22: SEO recommendations, client page (/seo-recommendations).
//
// The on-page findings of the baseline analysis as a checklist. Only a
// released list shows (v_portal_seo_task_list hides drafts from a client;
// the agency gets the same "released only" rule here so the page matches
// what the client sees). Otherwise the entitled "We're setting this up"
// placeholder. Session 23: gated on its own switch (the views need
// seo_recommendations = On). Preview shows the locked teaser from
// portal_seo_preview (summary fields only); Off is left out of the rail.
//
// SeoChecklistView is shared with the agency's SEO checklist tab (preview
// and released view). It renders from a list row + task rows and never
// talks to the database itself. Session 24: grouped into buckets (one per
// issue type, capped at 20 shown each and 100 in all; the rest is deferred
// and never reaches the client), with "Digital Lotus can fix this".

export const CATEGORY_LABEL = {
  meta_title: 'Meta title', meta_description: 'Meta description', headings: 'H1 and headings',
  indexability: 'Indexability', internal_links: 'Internal links', thin_content: 'Thin content',
  image_alt: 'Image alt text', schema: 'Schema', page_speed: 'Page speed', images: 'Images',
};
export const CATEGORY_ORDER = Object.keys(CATEGORY_LABEL);
export const OWNER_LABEL = { client: 'You', agency: 'Digital Lotus' };
const PRIORITIES = [['high', 'High priority'], ['medium', 'Medium priority'], ['low', 'Low priority']];

const isDone = (t) => !!(t.done_at || t.resolved_by_rerun_at);
// A bare YYYY-MM-DD is a calendar date: read it as local, not UTC midnight.
const fmtDate = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const paragraphs = (s) => String(s || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

export default function SeoRecommendations() {
  const { entitlements } = useAuth();
  const mode = seoRecsMode(entitlements);
  if (mode === 'preview') return <SeoPreviewPage />;
  if (mode === 'off') return <Placeholder />;
  return <SeoFullChecklist />;
}

function SeoFullChecklist() {
  const { activeId, isAgency } = useAuth();
  const [state, setState] = useState({ loading: true, list: null, tasks: [], buckets: [], error: null });
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    if (!activeId) return;
    const { data: lists, error } = await supabase.from('v_portal_seo_task_list').select('*')
      .eq('client_id', activeId).eq('status', 'released');
    if (error) { setState({ loading: false, list: null, tasks: [], error: error.message }); return; }
    const list = (lists || [])[0] || null;
    if (!list) { setState({ loading: false, list: null, tasks: [], error: null }); return; }
    const [{ data: tasks, error: tErr }, { data: buckets, error: bErr }] = await Promise.all([
      supabase.from('v_portal_seo_tasks').select('*').eq('list_id', list.list_id).order('sort'),
      supabase.from('v_portal_seo_buckets').select('*').eq('list_id', list.list_id),
    ]);
    if (tErr) { setState({ loading: false, list, tasks: [], buckets: [], error: tErr.message }); return; }
    // The agency's view includes hidden and deferred rows; the client never sees them.
    // Without the bucket view (database not migrated yet) the counts come from the tasks.
    setState({ loading: false, list, tasks: (tasks || []).filter((t) => !t.hidden && !t.deferred),
               buckets: bErr ? null : (buckets || []), error: null });
  }, [activeId]);

  useEffect(() => { setState((s) => ({ ...s, loading: true })); load(); }, [load]);

  // First open clears the client's Inbox item. A no-op for the agency.
  useEffect(() => {
    const l = state.list;
    if (!l || isAgency || l.client_first_viewed_at) return;
    supabase.rpc('portal_seo_mark_viewed', { p_list_id: l.list_id }).then(({ error }) => {
      if (!error) setState((s) => (s.list ? { ...s, list: { ...s.list, client_first_viewed_at: new Date().toISOString() } } : s));
    });
  }, [state.list, isAgency]);

  // One page at a time: the bucket counts are re-read after each tick so the
  // progress bars stay exact.
  async function onToggle(task, done) {
    const stamp = done ? new Date().toISOString() : null;
    setState((s) => ({ ...s, tasks: s.tasks.map((t) => (t.task_id === task.task_id ? { ...t, done_at: stamp } : t)) }));
    const { error } = await supabase.rpc('portal_seo_toggle_task', { p_task_id: task.task_id, p_done: done });
    if (error) {
      setState((s) => ({ ...s, tasks: s.tasks.map((t) => (t.task_id === task.task_id ? { ...t, done_at: task.done_at } : t)) }));
      setBanner({ tone: 'err', text: `Couldn't save that tick: ${error.message}` });
    } else {
      setBanner(null);
      const { data: buckets } = await supabase.from('v_portal_seo_buckets').select('*').eq('list_id', task.list_id);
      if (buckets) setState((s) => ({ ...s, buckets }));
    }
  }

  // Session 24: "Ask Digital Lotus to fix this" (the client makes the fixes).
  async function onRequestFix(task) {
    const { error } = await supabase.rpc('portal_seo_request_fix', { p_task_id: task.task_id });
    if (error) return { error: error.message };
    setState((s) => ({ ...s, tasks: s.tasks.map((t) => (t.task_id === task.task_id ? { ...t, fix_requested: true } : t)) }));
    return {};
  }

  if (state.loading) return <p className="lede">Loading your recommendations…</p>;
  if (state.error) return <div className="empty"><h2>Couldn't load your recommendations</h2><p>{state.error}</p></div>;
  if (!state.list) return <Placeholder />;

  return (
    <>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
      <SeoChecklistView list={state.list} tasks={state.tasks} buckets={state.buckets} canTick={(t) => !!t.can_tick}
                        onToggle={onToggle} fixMode={isAgency ? null : 'client'} onRequestFix={isAgency ? null : onRequestFix} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Session 23: the locked Preview. Only a released list shows (the same gate as
// On); the RPC returns the top 5 issues, the open count and the count per
// category, nothing else.
// ---------------------------------------------------------------------------

// Plain-language counts: [one, many]
export const CATEGORY_PLAIN = {
  meta_title: ['page title', 'page titles'],
  meta_description: ['page description', 'page descriptions'],
  headings: ['page heading to fix', 'page headings to fix'],
  indexability: ['page Google can\'t reach', 'pages Google can\'t reach'],
  internal_links: ['page with no links to it', 'pages with no links to them'],
  thin_content: ['page that needs more content', 'pages that need more content'],
  image_alt: ['page with images missing descriptions', 'pages with images missing descriptions'],
  schema: ['page missing structured data', 'pages missing structured data'],
  page_speed: ['slow page', 'slow pages'],
  images: ['page with images missing descriptions', 'pages with images missing descriptions'],
};

function SeoPreviewPage() {
  const { activeId } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    if (!activeId) return;
    let live = true;
    setState({ loading: true, data: null, error: null });
    supabase.rpc('portal_seo_preview', { p_client_id: activeId }).then(({ data, error }) => {
      if (live) setState({ loading: false, data: data || null, error: error ? error.message : null });
    });
    return () => { live = false; };
  }, [activeId]);
  if (state.loading) return <p className="lede">Loading your recommendations…</p>;
  if (state.error) return <div className="empty"><h2>Couldn't load your recommendations</h2><p>{state.error}</p></div>;
  if (!state.data) return <Placeholder />;
  return <SeoPreviewView data={state.data} />;
}

export function SeoPreviewView({ data }) {
  // Session 24: counts per bucket (real totals, deferred included); the
  // category counts are the fallback for a database without the buckets.
  const cats = data.buckets
    ? data.buckets.map((b) => `${b.count} ${b.count === 1 ? (b.one || b.bucket_title) : (b.many || b.bucket_title)}`)
    : (data.categories || []).map((c) => {
      const [one, many] = CATEGORY_PLAIN[c.category] || [c.category, c.category];
      return `${c.count} ${c.count === 1 ? one : many}`;
    });
  const subject = `Unlock SEO recommendations: ${data.client_name || ''}`;
  return (
    <div className="gmb seo seo-locked">
      <header className="gmb-head">
        <SectionHeading title="SEO recommendations"
          explainer="seo_recommendations" label="your SEO list" />
        <p className="gmb-intro">Here are the biggest issues we see on your website.</p>
      </header>
      <ol className="seo-top-issues">
        {(data.top || []).map((t, i) => (
          <li key={i} className="seo-top-issue">
            <div className="gmb-task-title">{t.title}</div>
            {t.line && <p className="hint" style={{ margin: '4px 0 0' }}>{t.line}</p>}
          </li>
        ))}
      </ol>
      <div className="progress-card">
        <div className="progress-title">We found {data.total} thing{data.total === 1 ? '' : 's'} to improve across your site.</div>
        {cats.length > 0 && <div className="progress-sub">{cats.join(' · ')}</div>}
      </div>
      <div className="empty seo-locked-cta">
        <p>
          Your full checklist shows everything to fix on your website, page by page. Digital Lotus can also make these
          changes for you. This service is locked right now.
        </p>
        <a className="btn btn-primary" href={`mailto:${ASK_EMAIL}?subject=${encodeURIComponent(subject)}`}>Contact Digital Lotus</a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared view. `canTick(task)` decides whether a checkbox is enabled;
// `onToggle` null = preview (checkboxes switched off).
//
// Session 24: buckets. One card per issue type with its plain title, "N
// found, M shown now" when part of it is deferred, and a progress bar (done
// of shown). Opening a card lists its pages in page_rank order (priority,
// Search Console impressions, home page, shallowest address, A to Z).
// There is no "mark all done" anywhere: every page is ticked on its own.
//   buckets       rows of v_portal_seo_buckets for this list (found / shown /
//                 done / deferred); without them the counts come from `tasks`
//   deferredTasks agency only: the deferred rows, shown read-only per bucket
//   fixMode       'client' = the fix labels and "Ask Digital Lotus to fix
//                 this"; null = none (the agency passes renderTaskExtra)
//   onRequestFix  client: (task) => Promise
//   renderTaskExtra agency: (task) => node under the task title row
// ---------------------------------------------------------------------------

const PRIO_ORDER = { high: 0, medium: 1, low: 2 };

export function bucketsFrom(tasks, buckets) {
  const by = new Map();
  for (const t of tasks) {
    const k = t.issue_type || String(t.issue_key || '').split('|')[1] || t.category;
    if (!by.has(k)) by.set(k, []);
    by.get(k).push(t);
  }
  const meta = new Map((buckets || []).map((b) => [b.issue_type, b]));
  const keys = new Set([...by.keys(), ...meta.keys()]);
  return [...keys].map((k) => {
    const items = (by.get(k) || []).slice().sort((a, b) => (a.page_rank ?? 0) - (b.page_rank ?? 0) || a.sort - b.sort);
    const m = meta.get(k);
    const first = items[0] || {};
    const best = items.reduce((p, t) => Math.min(p, PRIO_ORDER[t.priority] ?? 2), m ? (PRIO_ORDER[m.best_priority] ?? 2) : 2);
    return {
      key: k, items,
      title: m?.bucket_title || first.bucket_title || k,
      sort: m?.bucket_sort ?? first.bucket_sort ?? 9999,
      one: m?.count_one || 'item', many: m?.count_many || 'items',
      found: m ? m.found : items.length,
      shown: m ? m.shown : items.length,
      done: m ? m.done : items.filter(isDone).length,
      deferred: m?.deferred ?? null,
      best,
    };
  }).filter((b) => b.shown > 0 || b.found > 0)
    .sort((a, b) => a.best - b.best || a.sort - b.sort || a.key.localeCompare(b.key));
}

export function headerLine(bks, shownTotal, you = true) {
  const parts = bks.filter((b) => b.shown > 0).map((b) => `${b.shown} ${b.shown === 1 ? b.one : b.many}`);
  return `${shownTotal} correction${shownTotal === 1 ? '' : 's'} on ${you ? 'your' : 'the'} list${parts.length ? ': ' : ''}${parts.join(' · ')}`;
}

export function SeoChecklistView({ list, tasks, buckets = null, canTick, onToggle, preview = false, fixMode = null,
                                   onRequestFix = null, renderTaskExtra = null, deferredTasks = null, you = true }) {
  const [open, setOpen] = useState(() => new Set());
  const [howOpen, setHowOpen] = useState(() => new Set());
  const [openBuckets, setOpenBuckets] = useState(() => new Set());
  const [openDeferred, setOpenDeferred] = useState(() => new Set());

  useEffect(() => { setOpenBuckets(new Set()); setOpenDeferred(new Set()); }, [list.list_id]);

  const bks = useMemo(() => bucketsFrom(tasks, buckets), [tasks, buckets]);
  const deferredBy = useMemo(() => {
    const m = new Map();
    for (const t of deferredTasks || []) {
      const k = t.issue_type || String(t.issue_key || '').split('|')[1];
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(t);
    }
    for (const v of m.values()) v.sort((a, b) => (a.page_rank ?? 0) - (b.page_rank ?? 0));
    return m;
  }, [deferredTasks]);
  const counts = useMemo(() => ({ total: tasks.length, done: tasks.filter(isDone).length }), [tasks]);
  const pct = counts.total ? Math.round((counts.done / counts.total) * 100) : 0;

  const flip = (setter) => (id) => setter((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleOpen = flip(setOpen);
  const toggleHow = flip(setHowOpen);
  const toggleBucket = flip(setOpenBuckets);
  const toggleDeferred = flip(setOpenDeferred);
  const yours = tasks.filter((t) => t.owner_group === 'client');
  const ours = tasks.filter((t) => t.owner_group === 'agency');

  function taskRow(t, readOnly = false) {
    const resolved = !!t.resolved_by_rerun_at;
    const tickable = !readOnly && !preview && !!onToggle && canTick(t) && !resolved;
    const isOpen = open.has(t.task_id);
    const how = t.how_to || null;
    const howShown = howOpen.has(t.task_id);
    return (
      <li key={t.task_id} className={`gmb-task ${isDone(t) ? 'done' : ''} owner-${t.owner_group} ${readOnly ? 'seo-deferred' : ''}`}>
        <div className="gmb-task-row">
          <input type="checkbox" className="gmb-check" checked={isDone(t)} disabled={!tickable}
                 aria-label={`${isDone(t) ? 'Mark not done' : 'Mark done'}: ${t.title}`}
                 title={tickable ? undefined : readOnly ? 'Not on the list yet: it comes on at the next rerun'
                   : resolved ? 'Found fixed when we re-ran the analysis'
                   : preview ? 'Preview only' : `${OWNER_LABEL[t.owner_group]} ticks this one`}
                 onChange={(e) => tickable && onToggle(t, e.target.checked)} />
          <button type="button" className="gmb-task-title" aria-expanded={isOpen} onClick={() => toggleOpen(t.task_id)}>
            {t.title}
          </button>
          <div className="gmb-task-chips">
            {resolved && <span className="gmb-chip seo-resolved">Resolved ✓ (found fixed on {fmtDate(t.resolved_by_rerun_at)})</span>}
            {readOnly && <span className="gmb-chip seo-deferred-chip">Not on the list yet</span>}
            <span className={`gmb-chip gmb-owner owner-${t.owner_group}`}>{OWNER_LABEL[t.owner_group]}</span>
            {t.effort && <span className="gmb-chip gmb-effort">{t.effort}</span>}
            <button type="button" className="link-btn gmb-more" onClick={() => toggleOpen(t.task_id)}>{isOpen ? 'Less' : 'Details'}</button>
          </div>
        </div>
        {fixMode === 'client' && !readOnly && <ClientFixLine task={t} onRequestFix={onRequestFix} />}
        {renderTaskExtra && renderTaskExtra(t, readOnly)}
        {isOpen && (
          <div className="gmb-task-detail">
            {t.page_url && <p className="seo-page"><a href={t.page_url} target="_blank" rel="noopener noreferrer">{t.page_url}</a></p>}
            <h4>What we found</h4>{paragraphs(t.found).map((x, i) => <p key={i}>{x}</p>)}
            {t.matters && <><h4>Why it matters</h4>{paragraphs(t.matters).map((x, i) => <p key={i}>{x}</p>)}</>}
            <h4>What to do</h4>{paragraphs(t.fix).map((x, i) => <p key={i}>{x}</p>)}
            {t.done_when && <><h4>You'll know it's done when</h4>{paragraphs(t.done_when).map((x, i) => <p key={i}>{x}</p>)}</>}
          </div>
        )}
        {how && Array.isArray(how.steps) && how.steps.length > 0 && (
          <div className="seo-howto">
            <button type="button" className="link-btn" aria-expanded={howShown} onClick={() => toggleHow(t.task_id)}>
              {howShown ? '▾' : '▸'} {how.label || 'How to do this'}
            </button>
            {howShown && (
              <div className="seo-howto-body">
                {how.note && <p className="hint">{how.note}</p>}
                <ol>{how.steps.map((x, i) => <li key={i}>{x}</li>)}</ol>
              </div>
            )}
          </div>
        )}
      </li>
    );
  }

  return (
    <div className="gmb seo">
      <header className="gmb-head">
        <SectionHeading title="SEO recommendations"
          explainer="seo_recommendations" label="your SEO list" />
        <p className="gmb-intro">
          From our analysis of your website{list.baseline_date ? ` on ${fmtDate(list.baseline_date)}` : ''}. Each item points at a page and
          what we found there, with step-by-step instructions. Tick items off as they're done.
        </p>
      </header>

      <div className="progress-card gmb-progress">
        <div className="progress-head">
          <div>
            <div className="progress-title">{headerLine(bks, counts.total, you)}</div>
            <div className="progress-sub">
              {[`${counts.done} of ${counts.total} done`,
                yours.length ? `You: ${yours.filter(isDone).length} of ${yours.length}` : null,
                ours.length ? `Digital Lotus: ${ours.filter(isDone).length} of ${ours.length}` : null].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div className="gmb-pct">{pct}%</div>
        </div>
        <div className="progress-bar" role="progressbar" aria-valuenow={counts.done} aria-valuemin={0} aria-valuemax={counts.total}>
          <span style={{ width: `${pct}%` }} />
        </div>
      </div>
      {preview && <p className="hint gmb-preview-note">Preview: the checkboxes are switched off here. The client ticks the items tagged "You".</p>}

      {bks.length === 0 ? (
        <p className="results-empty">Nothing here.</p>
      ) : (
        <ul className="seo-buckets">
          {bks.map((b) => {
            const bpct = b.shown ? Math.round((b.done / b.shown) * 100) : 0;
            const isOpenB = openBuckets.has(b.key);
            const def = deferredBy.get(b.key) || [];
            const defOpen = openDeferred.has(b.key);
            return (
              <li key={b.key} className={`seo-bucket ${b.shown && b.done === b.shown ? 'complete' : ''}`}>
                <button type="button" className="seo-bucket-head" aria-expanded={isOpenB} onClick={() => toggleBucket(b.key)}>
                  <span className="seo-bucket-title">{isOpenB ? '▾' : '▸'} {b.title}</span>
                  <span className="seo-bucket-count">
                    {b.found > b.shown ? `${b.found} found, ${b.shown} shown now` : `${b.shown} ${b.shown === 1 ? b.one : b.many}`}
                  </span>
                  <span className="seo-bucket-pct">{bpct}% fixed</span>
                </button>
                <div className="progress-bar seo-bucket-bar" role="progressbar" aria-valuenow={b.done} aria-valuemin={0} aria-valuemax={b.shown}
                     aria-label={`${b.title}: ${b.done} of ${b.shown} fixed`}>
                  <span style={{ width: `${bpct}%` }} />
                </div>
                {isOpenB && (
                  <ul className="gmb-tasks seo-bucket-tasks">
                    {b.items.map((t) => taskRow(t))}
                  </ul>
                )}
                {isOpenB && b.found > b.shown && b.deferred == null && (
                  <p className="hint seo-bucket-more">The other {b.found - b.shown} come onto your list as these get fixed, after our next check of your site.</p>
                )}
                {deferredTasks && def.length > 0 && (
                  <div className="seo-deferred-block">
                    <button type="button" className="link-btn" aria-expanded={defOpen} onClick={() => toggleDeferred(b.key)}>
                      {defOpen ? '▾' : '▸'} {def.length} deferred (not on the client's list yet, read-only)
                    </button>
                    {defOpen && <ul className="gmb-tasks">{def.map((t) => taskRow(t, true))}</ul>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Session 24: the client's fix line under a task. Never shows the capability
// map, the channel or a proposal: only "Digital Lotus can fix this".
function ClientFixLine({ task, onRequestFix }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!task.fixable_by_us || isDone(task)) return null;
  if (task.owner_group === 'agency') return <p className="seo-fix-line">Digital Lotus is handling this.</p>;
  if (task.fix_requested) return <p className="seo-fix-line"><span className="gmb-chip seo-fix-requested">Requested</span> Digital Lotus can fix this. We'll take it from here.</p>;
  async function ask() {
    setBusy(true); setErr(null);
    const res = onRequestFix ? await onRequestFix(task) : null;
    setBusy(false);
    if (res && res.error) setErr(res.error);
  }
  return (
    <p className="seo-fix-line">
      Digital Lotus can fix this.{' '}
      <button type="button" className="btn btn-small" onClick={ask} disabled={busy || !onRequestFix}>
        {busy ? 'Asking…' : 'Ask Digital Lotus to fix this'}
      </button>
      {err && <span className="save-status save-err" role="alert">Couldn't ask: {err}</span>}
    </p>
  );
}
